import { PlaneEntryProps, ProfileType } from '@ixicare/resources';
import React, { Dispatch, SetStateAction } from 'react';
import { GoToMarkerEvent } from '~components/Map/DynamicMap';
import { t } from '~i18n';
import { DBGWithType } from '~modules/devices/components/DeviceHealthTable/types';
import { useDeviceDetail } from '../../../../modules/sites/hooks/useDeviceDetail';
import { WEARABLE_OR_BUTTON_TYPES } from '~modules/devices/utils/constants';
import { useDeviceArchive } from '../../../../modules/sites/hooks/useDeviceArchive';
import { useStores } from '~store/utils';
import { editAction, deleteAction } from '~components/Tables/DeviceTables/deviceTableActions';
import { GenericTable } from '~components/Tables/DeviceTables/GenericTable';
import { useTableRowActions } from '~components/Tables/DeviceTables/hooks/useTableRowActions';
import { BaseTableConfig, WithTransformer, WithColumnManager } from '../types';
import { DeleteDeviceModal } from '~modules/devices/components/DeviceModals/DeleteDeviceModal';
import { UpdateDeviceModal } from '~modules/devices/components/DeviceModals/UpdateDeviceModal';

interface PlaneDeviceTableProps {
  plane: PlaneEntryProps;
  items: DBGWithType[];
  config: BaseTableConfig & WithTransformer<DBGWithType> & WithColumnManager<DBGWithType>;
  setGoToDevice: Dispatch<SetStateAction<GoToMarkerEvent | undefined>>;
  onRefresh: () => void;
}

const PlaneDeviceTable: React.FC<PlaneDeviceTableProps> = ({ plane, items, config, setGoToDevice, onRefresh }) => {
  const { notifications, auth } = useStores();

  const isEmployee = auth.userType === ProfileType.WatcherrEmployee;

  const [selectedRow, setSelectedRow] = React.useState<DBGWithType>();
  React.useEffect(() => {
    if (!selectedRow) {
      return;
    }

    if (!selectedRow.installationCoords) {
      return;
    }

    setGoToDevice({
      id: `${selectedRow.type.toLowerCase()}::${selectedRow.id}`,
      lat: selectedRow?.installationCoords?.y,
      long: selectedRow?.installationCoords?.x,
    });

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [selectedRow]);

  const { selectedDevices, setSelectedDevices, selectedDeviceDetail, handleCloseDetail } = useDeviceDetail(items);

  const { devicesToArchive, setDevicesToArchive, deleteMutation } = useDeviceArchive({ onRefresh, notifications });

  const { actionProps, onActionClick, isActionVisible } = useTableRowActions(items, [
    {
      icon: 'residence-map',
      label: t('devices.table.map'),
      // TODO(bulk): acts on the first selected row until bulk behavior is wired.
      onClick: (devices) => {
        if (devices[0]) setSelectedRow(devices[0]);
      },
      isVisible: (device) => !!plane.cdnUrl && !!device.installationCoords,
    },
    editAction((devices) => setSelectedDevices(devices)),
    deleteAction((devices) => setDevicesToArchive(devices)),
  ]);

  const rows = React.useMemo(() => {
    return items.map((x) => [x.macAddress, x.type && t(`device.types.${x.type.toLowerCase()}`), x.description]);
  }, [items]);

  return (
    <>
      <GenericTable
        config={config}
        rows={rows ?? []}
        actions={actionProps}
        onActionClick={onActionClick}
        isActionVisible={isActionVisible}
        getRowLabel={(i) => items[i]?.macAddress}
        getRowKey={(i) => String(items[i]?.id ?? i)}
      />
      {selectedDevices && selectedDeviceDetail && (
        <UpdateDeviceModal
          type={selectedDeviceDetail.type}
          devices={selectedDevices}
          onClose={handleCloseDetail}
          showSiteSelect={WEARABLE_OR_BUTTON_TYPES.includes(selectedDeviceDetail.type)}
          onRefresh={onRefresh}
        />
      )}
      {devicesToArchive.length > 0 && (
        <DeleteDeviceModal
          devices={devicesToArchive}
          showHardDelete={isEmployee}
          onClose={() => setDevicesToArchive([])}
          onDelete={(hardDelete) => deleteMutation.mutate(hardDelete)}
          isPending={deleteMutation.isPending}
        />
      )}
    </>
  );
};

export default PlaneDeviceTable;
